import React, { useContext, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';

import {
  BrowserProps,
  MakePageResponseType,
  MakePageV3ResponseType,
  TemplateType,
} from '../../../types';

import { fetchTemplates, getAvatarPage } from '../../../lib/API';
import { CMSDimensionType, BaseAppType } from '../../../constants/common';
import useLazyload from 'src/hooks/useLazyload';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import { registerAndSendPageViewEvent } from '@invideoio/web-shared/utils/analytics/Register';
import { Referrer } from '@invideoio/web-shared/utils/utils.constant';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import {
  attachTemplates,
  extractTemplateDimensions,
  extractTemplateIds,
} from '@utils/common.utils';
import MakeSEO from '@components/common/SEO/MakeSEO';
import { DispatchContext } from '@store/store';
import { setEditorParams, setRedirectionLink } from '@store/actions';
import useTranslation from 'next-translate/useTranslation';
import MakePageContainerV3 from '@layouts/MakePageContainerV3';

interface AvatarProps extends BrowserProps {
  avatarPageState: MakePageResponseType & MakePageV3ResponseType;
}

const Avatar = (props: AvatarProps) => {
  const { avatarPageState } = props;

  useLazyload();

  const router = useRouter();
  const dispatch = useContext(DispatchContext);

  const { lang } = useTranslation('make');

  const url_path =
    router.locale === 'en'
      ? router.asPath
      : `/${router.locale}${router.asPath}`;

  useEffect(() => {
    AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    registerAndSendPageViewEvent(PageType.AI, {
      location: window.location.href,
    });

    if (avatarPageState.heroSection?.CTAUrl) {
      dispatch(setRedirectionLink(avatarPageState.heroSection?.CTAUrl));
    }

    if (avatarPageState.ExtraData) {
      const { searchTerm, tabType, categoryTray } = avatarPageState.ExtraData;

      dispatch(setEditorParams({ searchTerm, tabType, categoryTray }));
    }
  }, []);

  return (
    <React.Fragment>
      <MakeSEO
        seo={avatarPageState.SEO}
        url={url_path}
        FAQItems={avatarPageState.FaqItems}
        HowToSchema={avatarPageState.HowToSchema}
        locale={lang}
      />

      {avatarPageState && (
        <MakePageContainerV3 makePageState={avatarPageState} />
      )}
    </React.Fragment>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let avatarPageState = null;
  try {
    const slug = ctx.query.id as string;

    const appName = process.env.NEXT_PUBLIC_APP_NAME;

    if (appName === 'invideo') {
      try {
        avatarPageState = await getAvatarPage(slug, ctx.locale);
      } catch (error) {
        console.log(`Unable to fetch ai post`);
      }
    }

    if (
      avatarPageState &&
      avatarPageState.templateSlide_1 &&
      avatarPageState.templateSlide_2 &&
      avatarPageState.templateSlide_3 &&
      avatarPageState.templateSlide_4
    ) {
      const ids = extractTemplateIds(
        avatarPageState.templateSlide_1,
        avatarPageState.templateSlide_2,
        avatarPageState.templateSlide_3,
        avatarPageState.templateSlide_4,
      );
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        avatarPageState.templateSlide_1,
        avatarPageState.templateSlide_2,
        avatarPageState.templateSlide_3,
        avatarPageState.templateSlide_4,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);

      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      avatarPageState = {
        ...avatarPageState,
        ...attachTemplates({
          templateSlide1: avatarPageState.templateSlide_1,
          templateSlide2: avatarPageState.templateSlide_2,
          templateSlide3: avatarPageState.templateSlide_3,
          templateSlide4: avatarPageState.templateSlide_4,
          images: templates,
        }),
      };
    }

    //mini video wall slide
    if (avatarPageState && avatarPageState.videoWallSlide) {
      const ids = extractTemplateIds(avatarPageState.videoWallSlide);
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        avatarPageState.videoWallSlide,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);
      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      const attachedTemplates = attachTemplates({
        templateSlide1: avatarPageState.videoWallSlide,
        images: templates,
      });

      avatarPageState = {
        ...avatarPageState,
        videoWallSlide: attachedTemplates.templateSlide_1,
      };
    }
  } catch (error) {
    console.log(error);
    return { notFound: true };
  }
  return { props: { avatarPageState } };
}

export default Avatar;
